import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { requestTicketsListRefresh } from "../../utils/ticketRealtime";
import {
  consumePendingPushTicketUuid,
  getCapacitorPlugin,
  getMobilePushAppVariant,
  getMobilePushChannelConfig,
  getMobilePushChannelId,
  getMobilePushPlatform,
  getNotificationTicketUuid,
  getOrCreateMobilePushDeviceId,
  isNativeMobilePushRuntime,
  normalizeMobilePushPayload,
  storePendingPushTicketUuid
} from "../../utils/mobilePushRuntime";

const buildNotificationRoute = ticketUuid => {
  const normalizedTicketUuid = String(ticketUuid || "").trim();

  if (!normalizedTicketUuid) {
    return "";
  }

  return `/tickets/${normalizedTicketUuid}`;
};

const MobilePushManager = () => {
  const { isAuth, user } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const [pushToken, setPushToken] = useState("");
  const initializedRef = useRef(false);
  const syncedSignatureRef = useRef("");
  const localNotificationIdsRef = useRef(new Set());
  const runtimeEnabled = useMemo(() => isNativeMobilePushRuntime(), []);

  useEffect(() => {
    if (!runtimeEnabled || !isAuth || !user?.id) {
      return;
    }

    const pendingTicketUuid = consumePendingPushTicketUuid();
    const targetRoute = buildNotificationRoute(pendingTicketUuid);

    if (!targetRoute || targetRoute === location.pathname) {
      return;
    }

    history.push(targetRoute);
  }, [history, isAuth, location.pathname, runtimeEnabled, user?.id]);

  useEffect(() => {
    if (!runtimeEnabled || initializedRef.current) {
      return undefined;
    }

    const pushNotifications = getCapacitorPlugin("PushNotifications");
    const localNotifications = getCapacitorPlugin("LocalNotifications");

    if (!pushNotifications) {
      return undefined;
    }

    initializedRef.current = true;
    let disposed = false;
    const listenerHandles = [];

    const registerListener = async (plugin, eventName, handler) => {
      if (!plugin || typeof plugin.addListener !== "function") {
        return;
      }

      const handle = await plugin.addListener(eventName, handler);
      listenerHandles.push(handle);
    };

    const openTicketFromPayload = payload => {
      const ticketUuid = getNotificationTicketUuid(payload);
      const targetRoute = buildNotificationRoute(ticketUuid);

      if (!targetRoute) {
        return;
      }

      storePendingPushTicketUuid(ticketUuid);

      if (isAuth && user?.id && history.location.pathname !== targetRoute) {
        history.push(targetRoute);
      }
    };

    const refreshTicketsListFromPayload = (payload, reason = "mobile-push") => {
      const ticketUuid = getNotificationTicketUuid(payload);
      requestTicketsListRefresh({
        ticketUuid: ticketUuid || null,
        reason
      });
    };

    const showForegroundNotification = async payload => {
      if (!localNotifications || typeof localNotifications.schedule !== "function") {
        return;
      }

      const targetRoute = buildNotificationRoute(payload.ticketUuid);

      if (
        document.visibilityState !== "visible" ||
        !targetRoute ||
        history.location.pathname === targetRoute
      ) {
        return;
      }

      const baseNotificationId =
        payload.messageId || Math.round(Date.now() % 2147483000);

      if (localNotificationIdsRef.current.has(baseNotificationId)) {
        return;
      }

      localNotificationIdsRef.current.add(baseNotificationId);

      try {
        await localNotifications.schedule({
          notifications: [
            {
              id: baseNotificationId,
              title: payload.title || "AgilZap",
              body: payload.body || "Voce recebeu uma nova mensagem.",
              smallIcon: "ic_stat_equipechat",
              channelId: getMobilePushChannelId(),
              extra: payload.data || {},
            },
          ],
        });
      } catch (error) {
        console.error("[EquipeChat Teste] Erro ao exibir notificacao local", error);
      }
    };

    const initializePushRuntime = async () => {
      try {
        if (
          localNotifications &&
          typeof localNotifications.createChannel === "function"
        ) {
          await localNotifications.createChannel(getMobilePushChannelConfig());
        }

        if (
          pushNotifications &&
          typeof pushNotifications.createChannel === "function"
        ) {
          await pushNotifications.createChannel(getMobilePushChannelConfig());
        }

        const localPermissionStatus =
          localNotifications &&
          typeof localNotifications.checkPermissions === "function"
            ? await localNotifications.checkPermissions()
            : null;

        if (
          localNotifications &&
          localPermissionStatus?.display === "prompt" &&
          typeof localNotifications.requestPermissions === "function"
        ) {
          await localNotifications.requestPermissions();
        }

        const permissionStatus =
          typeof pushNotifications.checkPermissions === "function"
            ? await pushNotifications.checkPermissions()
            : { receive: "granted" };

        let receivePermission = permissionStatus?.receive || "prompt";

        if (
          receivePermission === "prompt" &&
          typeof pushNotifications.requestPermissions === "function"
        ) {
          const permissionRequest = await pushNotifications.requestPermissions();
          receivePermission = permissionRequest?.receive || receivePermission;
        }

        if (receivePermission === "granted") {
          await pushNotifications.register();
          return;
        }

        console.warn(
          "[EquipeChat Teste] Permissao de push nao concedida no dispositivo."
        );
      } catch (error) {
        console.error("[EquipeChat Teste] Falha ao inicializar o push", error);
      }
    };

    registerListener(pushNotifications, "registration", tokenEvent => {
      if (disposed) {
        return;
      }

      const nextToken = String(tokenEvent?.value || "").trim();

      if (nextToken) {
        setPushToken(nextToken);
      }
    });

    registerListener(pushNotifications, "registrationError", errorEvent => {
      console.error("[EquipeChat Teste] Erro no registro do push", errorEvent);
    });

    registerListener(pushNotifications, "pushNotificationReceived", async notification => {
      if (disposed) {
        return;
      }

      const payload = normalizeMobilePushPayload(notification);
      refreshTicketsListFromPayload(payload, "mobile-push-received");
      await showForegroundNotification(payload);
    });

    registerListener(pushNotifications, "pushNotificationActionPerformed", actionEvent => {
      if (disposed) {
        return;
      }

      const payload = actionEvent?.notification?.data || {};
      refreshTicketsListFromPayload(payload, "mobile-push-opened");
      openTicketFromPayload(payload);
    });

    registerListener(
      localNotifications,
      "localNotificationActionPerformed",
      actionEvent => {
        if (disposed) {
          return;
        }

        const payload = actionEvent?.notification?.extra || {};
        refreshTicketsListFromPayload(payload, "mobile-local-push-opened");
        openTicketFromPayload(payload);
      }
    );

    initializePushRuntime();

    return () => {
      disposed = true;

      listenerHandles.forEach(handle => {
        if (handle && typeof handle.remove === "function") {
          handle.remove();
        }
      });
    };
  }, [history, isAuth, runtimeEnabled, user?.id]);

  useEffect(() => {
    if (!runtimeEnabled || !isAuth || !user?.id || !user?.companyId || !pushToken) {
      return undefined;
    }

    const syncSignature = JSON.stringify({
      companyId: user.companyId,
      userId: user.id,
      token: pushToken,
      deviceId: getOrCreateMobilePushDeviceId(),
    });

    if (syncSignature === syncedSignatureRef.current) {
      return undefined;
    }

    let cancelled = false;

    const syncPushDevice = async () => {
      try {
        await api.post("/mobile/push/devices", {
          token: pushToken,
          deviceId: getOrCreateMobilePushDeviceId(),
          platform: getMobilePushPlatform(),
          appVariant: getMobilePushAppVariant(),
          appVersion: "0.1.0",
        });

        if (!cancelled) {
          syncedSignatureRef.current = syncSignature;
        }
      } catch (error) {
        console.error("[EquipeChat Teste] Falha ao sincronizar o token push", error);
      }
    };

    syncPushDevice();

    return () => {
      cancelled = true;
    };
  }, [isAuth, pushToken, runtimeEnabled, user?.companyId, user?.id]);

  return null;
};

export default MobilePushManager;
